/**
 * A semana em quadradinhos: quando alguém pode dar aula.
 *
 * Usada nas duas pontas — o professor preenchendo pelo link e a coordenação
 * ajustando a de qualquer professor —, para as duas verem exatamente a mesma
 * coisa. Um quadradinho é um horário de aula dos esquemas, com o horário
 * escrito em horas (07:30–08:20), nunca em minutos.
 *
 * Clicar no nome do dia marca ou desmarca a coluna inteira daquele turno;
 * clicar no horário, a linha inteira. É o gesto de quem diz "terça de manhã
 * eu não posso" — ninguém pensa disponibilidade quadradinho a quadradinho.
 */

import { chaveDoTempo, hora, linhasDoTurno, type TempoDoFormulario } from '../formulario'

const DIA = ['', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo']

export function SemanaDeDisponibilidade({
  turnos,
  tempos,
  marcados,
  onMudar,
  somenteLeitura = false,
}: {
  turnos: { id: string; nome: string }[]
  tempos: TempoDoFormulario[]
  marcados: ReadonlySet<string>
  /** Recebe as chaves e o novo estado de todas elas. */
  onMudar: (chaves: string[], pode: boolean) => void
  somenteLeitura?: boolean
}) {
  const dias = [...new Set(tempos.map((t) => t.dia))].sort((a, b) => a - b)
  const existe = new Set(tempos.map(chaveDoTempo))

  // Linha ou coluna: se tudo já está liberado, o clique fecha; senão, abre.
  const alternarGrupo = (chaves: string[]) => {
    if (chaves.length === 0) return
    onMudar(chaves, !chaves.every((k) => marcados.has(k)))
  }

  return (
    <div className="semana">
      {turnos
        .filter((t) => tempos.some((x) => x.turno_id === t.id))
        .map((turno) => {
          const doTurno = tempos.filter((t) => t.turno_id === turno.id)
          const livres = doTurno.filter((t) => marcados.has(chaveDoTempo(t))).length
          const linhas = linhasDoTurno(tempos, turno.id)
          return (
            <section key={turno.id} className="semana__turno">
              <header className="semana__cabeca">
                <h3>{turno.nome}</h3>
                <span className="field__hint">
                  pode em <strong className="mono">{livres}</strong> de{' '}
                  <span className="mono">{doTurno.length}</span> horários
                </span>
              </header>
              <div className="tabela-rolavel">
                <table className="semana__grade">
                  <thead>
                    <tr>
                      <th scope="col" className="semana__canto">
                        Horário
                      </th>
                      {dias.map((d) => {
                        const chaves = doTurno.filter((t) => t.dia === d).map(chaveDoTempo)
                        return (
                          <th key={d} scope="col">
                            {somenteLeitura || chaves.length === 0 ? (
                              DIA[d]
                            ) : (
                              <button
                                type="button"
                                className="semana__eixo"
                                title={`Marcar ou desmarcar ${DIA[d]?.toLowerCase()} inteira no turno ${turno.nome.toLowerCase()}`}
                                onClick={() => alternarGrupo(chaves)}
                              >
                                {DIA[d]}
                              </button>
                            )}
                          </th>
                        )
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.map((l) => {
                      const chaves = dias
                        .map((d) => chaveDoTempo({ dia: d, inicio: l.inicio, fim: l.fim }))
                        .filter((k) => existe.has(k))
                      const rotulo = `${hora(l.inicio)}–${hora(l.fim)}`
                      return (
                        <tr key={`${l.inicio}-${l.fim}`}>
                          <th scope="row" className="mono">
                            {somenteLeitura ? (
                              rotulo
                            ) : (
                              <button
                                type="button"
                                className="semana__eixo mono"
                                title={`Marcar ou desmarcar ${rotulo} em todos os dias`}
                                onClick={() => alternarGrupo(chaves)}
                              >
                                {rotulo}
                              </button>
                            )}
                          </th>
                          {dias.map((d) => {
                            const k = chaveDoTempo({ dia: d, inicio: l.inicio, fim: l.fim })
                            if (!existe.has(k)) return <td key={d} className="semana__vazio" />
                            const pode = marcados.has(k)
                            return (
                              <td key={d}>
                                <button
                                  type="button"
                                  className="semana__celula"
                                  aria-pressed={pode}
                                  disabled={somenteLeitura}
                                  aria-label={`${DIA[d]}, ${rotulo}: ${pode ? 'pode' : 'não pode'}`}
                                  onClick={() => onMudar([k], !pode)}
                                >
                                  {pode ? 'pode' : 'não pode'}
                                </button>
                              </td>
                            )
                          })}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )
        })}
    </div>
  )
}

/** A legenda, uma vez por tela. Forma além da cor: a palavra está na célula. */
export function LegendaDaSemana() {
  return (
    <p className="semana__legenda">
      <span className="semana__amostra semana__amostra--pode">pode</span> dá aula neste horário
      <span className="semana__amostra">não pode</span> não dá — o sistema não põe aula aqui
    </p>
  )
}
